import React from 'react';
import { Car } from 'lucide-react';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { cn } from '../../../lib/utils';

const COCHERA_TIPOS = new Set(['cochera', 'cochera_estandar']);

const esCochera = (u) => {
  if (!u) return false;
  const tipo = (u.tipo || '').toLowerCase();
  if (COCHERA_TIPOS.has(tipo)) return true;
  if (u.bucket_cochera) return true;
  const nombre = (u.nombre_unidad || '').toUpperCase();
  if (/^C\d+/.test(nombre)) return true;
  return false;
};

const formatNumber = (n) => {
  if (n == null || n === '') return '';
  const num = Number(n);
  if (!isFinite(num)) return '';
  return num.toLocaleString('es-AR', { maximumFractionDigits: 2 });
};

const parseNumber = (s) => {
  if (s == null || s === '') return 0;
  // Formato argentino: punto = miles, coma = decimal.
  const cleaned = String(s).replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  return isFinite(n) ? n : 0;
};

// Input que mantiene su propio texto mientras se escribe (permite tipear coma decimal
// y separadores de miles) y normaliza/formatea sólo al perder foco.
const CocheraValorInput = ({ value, disabled, onChange, testId }) => {
  const [focused, setFocused] = React.useState(false);
  const [text, setText] = React.useState(formatNumber(value));

  React.useEffect(() => {
    if (!focused) setText(formatNumber(value));
  }, [value, focused]);

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={text}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const sane = e.target.value.replace(/[^\d.,]/g, '');
        setText(sane);
        onChange(parseNumber(sane));
      }}
      onBlur={() => { setFocused(false); setText(formatNumber(value)); }}
      placeholder="0"
      disabled={disabled}
      data-testid={testId}
    />
  );
};

/**
 * CocherasModoSection — valoración POR CADA cochera del contrato.
 * Se oculta por completo si el contrato no tiene cocheras.
 *
 * Props:
 *  - unidadIds: string[] (ids de unidades del contrato)
 *  - unidadesInfo: [{unidad_id, nombre_unidad, tipo, bucket_cochera}]
 *  - valuacion: { [unidad_id]: { modo: 'estandar'|'propio', valor_base: {monto, moneda} } }
 *  - valorEstandarEdificio?: number (informativo)
 *  - monedaContrato?: 'ARS'|'USD'
 *  - onChange: (nuevoMapa) => void
 *  - disabled?: boolean
 */
export const CocherasModoSection = ({
  unidadIds = [],
  unidadesInfo = [],
  valuacion = {},
  valorEstandarEdificio = null,
  monedaContrato = 'ARS',
  onChange,
  disabled = false,
}) => {
  const unidadesById = React.useMemo(() => {
    const m = {};
    (unidadesInfo || []).forEach(u => { m[u.unidad_id] = u; });
    return m;
  }, [unidadesInfo]);

  const cocheras = React.useMemo(() =>
    (unidadIds || [])
      .map(uid => unidadesById[uid])
      .filter(u => esCochera(u)),
  [unidadIds, unidadesById]);

  if (cocheras.length === 0) return null;

  const getCfg = (uid) => valuacion?.[uid] || { modo: 'estandar', valor_base: null };

  const setCfg = (uid, cfg) => {
    const next = { ...(valuacion || {}) };
    next[uid] = cfg;
    onChange?.(next);
  };

  return (
    <div className="border border-violet-200 bg-violet-50/40 rounded-lg p-4 space-y-3" data-testid="cocheras-valuacion-section">
      <div className="flex items-start gap-2">
        <Car className="w-4 h-4 text-violet-700 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-violet-900">Valoración de cocheras</h4>
          <p className="text-xs text-violet-700 mt-0.5">
            Elegí cómo se valúa cada cochera. El ingreso de cada cochera se rinde a su propietario; la oficina recibe el resto.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {cocheras.map((c) => {
          const cfg = getCfg(c.unidad_id);
          const modo = cfg.modo || 'estandar';
          const moneda = cfg.valor_base?.moneda || monedaContrato || 'ARS';
          const monto = Number(cfg.valor_base?.monto) || 0;
          return (
            <div key={c.unidad_id} className="bg-white rounded border border-violet-100 p-3" data-testid={`cochera-valuacion-${c.unidad_id}`}>
              <div className="text-sm font-medium text-slate-800 mb-1.5">{c.nombre_unidad}</div>
              <RadioGroup
                value={modo}
                onValueChange={(v) => setCfg(c.unidad_id, v === 'estandar'
                  ? { modo: 'estandar', valor_base: null }
                  : { modo: 'propio', valor_base: cfg.valor_base || { monto: 0, moneda: monedaContrato } })}
                disabled={disabled}
                className="flex flex-col sm:flex-row gap-2 sm:gap-4"
              >
                <label className={cn("flex items-center gap-2 cursor-pointer", disabled && "cursor-not-allowed opacity-70")}>
                  <RadioGroupItem value="estandar" data-testid={`radio-estandar-${c.unidad_id}`} />
                  <span className="text-xs text-slate-700">
                    Valor estándar del edificio{valorEstandarEdificio ? ` ($ ${formatNumber(valorEstandarEdificio)})` : ''}
                  </span>
                </label>
                <label className={cn("flex items-center gap-2 cursor-pointer", disabled && "cursor-not-allowed opacity-70")}>
                  <RadioGroupItem value="propio" data-testid={`radio-propio-${c.unidad_id}`} />
                  <span className="text-xs text-slate-700">Valor propio/manual</span>
                </label>
              </RadioGroup>

              {modo === 'propio' && (
                <div className="mt-2 grid grid-cols-3 gap-2 max-w-md">
                  <div className="col-span-2">
                    <Label className="text-xs">Valor de cochera</Label>
                    <CocheraValorInput
                      value={monto}
                      disabled={disabled}
                      onChange={(num) => setCfg(c.unidad_id, { modo: 'propio', valor_base: { monto: num, moneda } })}
                      testId={`input-valor-propio-${c.unidad_id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Moneda</Label>
                    <Select
                      value={moneda}
                      onValueChange={(v) => setCfg(c.unidad_id, { modo: 'propio', valor_base: { monto, moneda: v } })}
                      disabled={disabled}
                    >
                      <SelectTrigger data-testid={`select-moneda-propio-${c.unidad_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ARS">ARS</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="col-span-3 text-[11px] text-violet-700">
                    Se ajusta con el mismo índice y frecuencia del contrato.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
